// Org-weite Devices-Sicht (TQ #5203 Phase 1)
//
// Cockpit-View aller Geraete der Organisation. Org-Owner sieht alle
// Geraete aller Mitarbeiter. Drawer zeigt Detail.
//
// Trennung zu IT-SCE-Hub:
//   - Cockpit (DIESE Page) = "alle meine Geraete (Org-Sicht)"
//   - Hub (TQ #5199)        = "dieses eine Geraet (Maschinen-Sicht)"
//
// Phase 1: Echte API-Calls gegen portal-api (Backend ist seit #5156 LIVE).
// Sparklines, Capabilities-Detail, Aria-Aktions-Buttons = Phase 2.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { IconType } from 'react-icons'
import { FaApple, FaWindows, FaLinux, FaMicrochip, FaDesktop } from 'react-icons/fa'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { DeviceActionsPanel } from '../components/devices/DeviceActionsPanel'
import { CommandConfirmPanel } from '../components/devices/CommandConfirmPanel'
import { DeviceGrantsPanel } from '../components/devices/DeviceGrantsPanel'
import { displayIdentity } from '../utils/identityDisplay'

interface Device {
  device_id: string
  hostname: string
  device_type: string // linux|windows|mac|embedded
  agent_version: string
  enrolled_at: string
  enrolled_via?: string // hub-jwt|cli-token
  last_seen_at: string
  status: string // online|offline|decommissioned|error
  tags: string[]
  description: string
  capabilities?: Record<string, unknown>
  /** TQ #5981/5982: keycloak UUID des Owners, NULL = shared/org-weit. */
  owner_user_id?: string | null
}

// TQ #5982: Org-Member fuer Owner-Resolution + Assignment-Dropdown.
interface OrgMember {
  keycloak_id: string
  email: string
  first_name?: string
  last_name?: string
  role: string
}

// ownerDisplay liefert den Anzeige-Namen fuer einen Owner.
function ownerDisplay(uuid: string | null | undefined, members: OrgMember[]): string {
  if (!uuid) return 'Geteilt (Org-weit)'
  const m = members.find(x => x.keycloak_id === uuid)
  if (!m) return uuid.slice(0, 8) + '…'
  const full = [m.first_name, m.last_name].filter(Boolean).join(' ').trim()
  return full || displayIdentity(m) || uuid.slice(0, 8) + '…'
}

interface DevicesResponse {
  devices: Device[]
}

interface EnrollTokenResponse {
  token: string
  expires_at: string
}

interface SeatInfo {
  seats_subscribed: number
  devices_enrolled: number
  has_subscription: boolean
}

const POLL_INTERVAL = 10_000

function fmtRelative(iso: string): string {
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60) return `vor ${sec}s`
  if (sec < 3600) return `vor ${Math.floor(sec / 60)} Min`
  if (sec < 86400) return `vor ${Math.floor(sec / 3600)} Std`
  return `vor ${Math.floor(sec / 86400)} Tagen`
}

function statusForDevice(d: Device): 'online' | 'warning' | 'offline' | 'decommissioned' {
  if (d.status === 'decommissioned') return 'decommissioned'
  const ageSec = (Date.now() - new Date(d.last_seen_at).getTime()) / 1000
  if (ageSec < 120) return 'online'
  if (ageSec < 300 || d.status === 'error') return 'warning'
  return 'offline'
}

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  online: { bg: '#dcfce7', text: '#166534', label: '● online' },
  warning: { bg: '#fef3c7', text: '#92400e', label: '◐ stale' },
  offline: { bg: '#e5e7eb', text: '#374151', label: '○ offline' },
  decommissioned: { bg: '#fee2e2', text: '#991b1b', label: '✕ stillgelegt' },
}

// TQ #8108: echte Produkt-/OS-Icons (react-icons brand SVGs) statt Emoji.
const deviceTypeIcons: Record<string, IconType> = {
  linux: FaLinux,
  windows: FaWindows,
  mac: FaApple,
  embedded: FaMicrochip,
}

function DeviceTypeIcon({ type, size = 24, color = '#4b5563' }: { type?: string; size?: number; color?: string }) {
  const Icon = (type && deviceTypeIcons[type]) || FaDesktop
  return <Icon size={size} color={color} aria-label={type ? `${type} device` : 'device'} />
}

interface OrganizationProp {
  organization: { slug: string; public_token?: string; role?: string }
  /** TQ #6962-T4: Personal-Mode (with.it-sce.de). Geräte werden user-scoped
      über GET /api/me/devices geladen (owner_user_id = JWT-Sub), Org-only
      Features (Seats, Member-Owner-Zuweisung, Enroll-Token) entfallen. */
  personalMode?: boolean
}

export default function Devices({ organization, personalMode = false }: OrganizationProp) {
  const { api, user } = useAuth()
  const [devices, setDevices] = useState<Device[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [drawerDevice, setDrawerDevice] = useState<Device | null>(null)
  const [showTokenModal, setShowTokenModal] = useState(false)
  // TQ #5982: Members fuer Owner-Display + Assignment-Dropdown.
  // Geladen einmal pro Org, klein genug fuer KMU-Sicht. Im Personal-Mode
  // gibt es keine Org-Mitglieder → leer.
  const [members, setMembers] = useState<OrgMember[]>([])

  // Im Personal-Mode ist der eingeloggte User immer "Owner" seiner eigenen
  // Geräte — Org-Owner-Rolle existiert dort nicht.
  const isOwner = personalMode ? true : organization.role === 'owner'
  const currentUserSub = user?.sub ?? null
  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null)

  useEffect(() => {
    if (!api || personalMode) return // Seats sind ein Org-Konzept
    api.get<SeatInfo>(`/api/org/${organization.public_token || organization.slug}/devices/seat-info`)
      .then(r => setSeatInfo(r.data))
      .catch(() => {})
  }, [api, personalMode, organization.slug, organization.public_token])

  useEffect(() => {
    if (!api || personalMode) return // keine Org-Mitglieder im Personal-Mode
    api.get<{ members?: OrgMember[]; users?: OrgMember[] }>(`/api/org/${organization.public_token || organization.slug}/members`)
      .then(r => {
        const raw = r.data?.members ?? r.data?.users ?? []
        setMembers(raw.filter(m => m.keycloak_id))
      })
      .catch(() => setMembers([]))
  }, [api, personalMode, organization.slug, organization.public_token])

  const load = useCallback(async () => {
    try {
      // TQ #6962-T4: Personal-Mode → user-scoped /api/me/devices (JWT-Sub),
      // sonst org-scoped /api/org/<slug>/devices wie bisher.
      const url = personalMode
        ? '/api/me/devices'
        : `/api/org/${organization.public_token || organization.slug}/devices`
      const r = await api!.get<DevicesResponse>(url)
      setDevices(r.data?.devices ?? [])
      setError(null)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? 'Geräte-Liste konnte nicht geladen werden.')
      setDevices([])
    }
  }, [api, personalMode, organization.slug, organization.public_token])

  useEffect(() => {
    void load()
    const t = setInterval(load, POLL_INTERVAL)
    return () => clearInterval(t)
  }, [load])

  const { active, decommissioned } = useMemo(() => {
    const a: Device[] = []
    const d: Device[] = []
    for (const dev of devices ?? []) {
      if (dev.status === 'decommissioned') d.push(dev)
      else a.push(dev)
    }
    return { active: a, decommissioned: d }
  }, [devices])

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1280, margin: '0 auto' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1f2937' }}>
            {personalMode ? 'Meine Geräte' : 'Geräte deiner Organisation'}
          </h1>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            {personalMode
              ? 'Aria sieht und kümmert sich um deine Geräte. '
              : 'Aria sieht und kümmert sich um eure Geräte. '}
            Workstations/Notebooks/Macs verbinden sich
            automatisch sobald IT-SCE-Hub installiert ist (
            <a href="https://with.it-sce.de/downloads/hub" target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>
              Hub herunterladen
            </a>
            ).
          </p>
        </div>
        {/* Headless-Enroll-Token ist heute org-scoped (POST /api/org/<slug>/devices/enroll-token).
            Im Personal-Mode (kein org_slug) ist der Pfad noch nicht verfügbar — Personal-User
            enrollen über den Hub (Hub-JWT, org_slug NULL + owner_user_id=Sub). */}
        {isOwner && !personalMode && (
          <button
            onClick={() => setShowTokenModal(true)}
            style={{
              padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none',
              borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
            }}
          >
            + Headless-Server hinzufügen
          </button>
        )}
      </header>

      {!personalMode && (
        <SeatBanner seatInfo={seatInfo} orgSlug={organization.public_token || organization.slug} />
      )}

      {error && (
        <div style={{
          padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, color: '#b91c1c', fontSize: '0.875rem', marginBottom: '1rem',
        }}>{error}</div>
      )}

      {devices === null && !error && (
        <p style={{ color: '#6b7280' }}>Lade Geräte …</p>
      )}

      {devices !== null && active.length === 0 && (
        <EmptyState isOwner={isOwner && !personalMode} onCreateToken={() => setShowTokenModal(true)} />
      )}

      {active.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {active.map((d) => (
            <DeviceCard
              key={d.device_id}
              device={d}
              members={members}
              currentUserSub={currentUserSub}
              onClick={() => setDrawerDevice(d)}
            />
          ))}
        </div>
      )}

      {decommissioned.length > 0 && (
        <details style={{ marginTop: '2rem' }}>
          <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: '0.875rem' }}>
            Archiv ({decommissioned.length} stillgelegt)
          </summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginTop: '0.5rem', opacity: 0.6 }}>
            {decommissioned.map((d) => (
              <DeviceCard
                key={d.device_id}
                device={d}
                members={members}
                currentUserSub={currentUserSub}
                onClick={() => setDrawerDevice(d)}
              />
            ))}
          </div>
        </details>
      )}

      {drawerDevice && (
        <DeviceDrawer
          device={drawerDevice}
          orgSlug={organization.public_token || organization.slug}
          isOperator={isOwner}
          // Owner-Zuweisung an Org-Mitglieder ergibt im Personal-Mode keinen
          // Sinn (kein Org-Kontext) — Drawer zeigt dann keine Admin-Sektion.
          isAdmin={isOwner && !personalMode}
          members={members}
          personalMode={personalMode}
          onClose={() => setDrawerDevice(null)}
          onOwnerChanged={(updatedDevice) => {
            setDevices(prev => prev?.map(d => d.device_id === updatedDevice.device_id ? updatedDevice : d) ?? prev)
            setDrawerDevice(updatedDevice)
          }}
          onLifecycleChange={(removed) => {
            void load()
            if (removed) setDrawerDevice(null)
          }}
        />
      )}

      {showTokenModal && (
        <TokenModal
          orgSlug={organization.slug}
          onClose={() => setShowTokenModal(false)}
          onSuccess={() => { void load(); setShowTokenModal(false) }}
        />
      )}
    </div>
  )
}

// SeatBanner — Slack/Atlassian-Muster: X von N Seats genutzt + Add-Seat-Link.
// Kein Lizenz-Aktivierungs-Tanz: Buchungs-Link geht direkt zum Marktplatz.
function SeatBanner({ seatInfo, orgSlug }: { seatInfo: SeatInfo | null; orgSlug: string }) {
  if (!seatInfo) return null

  const { seats_subscribed, devices_enrolled, has_subscription } = seatInfo
  const overCapacity = has_subscription && devices_enrolled > seats_subscribed
  const atCapacity = has_subscription && devices_enrolled === seats_subscribed && seats_subscribed > 0

  if (!has_subscription && devices_enrolled === 0) return null

  const bg = overCapacity ? '#fef2f2' : atCapacity ? '#fffbeb' : '#f0fdf4'
  const border = overCapacity ? '#fecaca' : atCapacity ? '#fde68a' : '#bbf7d0'
  const color = overCapacity ? '#b91c1c' : atCapacity ? '#92400e' : '#166534'

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0.625rem 1rem', background: bg, border: `1px solid ${border}`,
      borderRadius: 8, marginBottom: '1rem', fontSize: '0.875rem', color,
    }}>
      <span>
        {has_subscription
          ? `💺 ${devices_enrolled} von ${seats_subscribed} Seats genutzt`
          : `💺 ${devices_enrolled} Gerät${devices_enrolled !== 1 ? 'e' : ''} aktiv — kein Managed-Endpoint-Abo`}
        {overCapacity && ' — Kapazität überschritten'}
        {atCapacity && ' — alle Seats belegt'}
      </span>
      <a
        href={`/${orgSlug}/marketplace`}
        style={{ color, fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', marginLeft: '1rem', textDecoration: 'underline' }}
      >
        {has_subscription ? '+ Seat hinzubuchen' : 'Jetzt buchen'}
      </a>
    </div>
  )
}

function EmptyState({ isOwner, onCreateToken }: { isOwner: boolean; onCreateToken: () => void }) {
  return (
    <div style={{
      maxWidth: 640, margin: '3rem auto', padding: '2rem',
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, textAlign: 'center',
    }}>
      <div style={{ marginBottom: '0.5rem' }}><DeviceTypeIcon size={48} color="#9ca3af" /></div>
      <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937' }}>Noch kein Gerät verbunden</h2>
      <p style={{ color: '#4b5563', margin: '0.75rem auto 0.5rem', fontSize: '0.9375rem', lineHeight: 1.55, maxWidth: 500 }}>
        Geräte verbinden eure Workstations, Notebooks, Macs und Server mit dem Cockpit.
        So sieht Aria z.B. Update-Status, kann gezielt Befehle ausführen und Hilfe-Tickets
        am richtigen Gerät verankern.
      </p>
      <p style={{ color: '#6b7280', margin: '0 auto 1.5rem', fontSize: '0.875rem', lineHeight: 1.55, maxWidth: 500 }}>
        Für Desktop-Geräte installierst du den <strong>IT-SCE-Hub</strong> — der registriert sich
        automatisch. Für Headless-Server holst du dir einen einmal-Token und enrollst
        per Shell-Befehl.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <a href="https://with.it-sce.de/downloads/hub?os=linux" target="_blank" rel="noreferrer"
           style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, color: '#1f2937', textDecoration: 'none', fontSize: '0.875rem' }}><FaLinux style={{ verticalAlign: 'text-bottom', marginRight: 5 }} />Linux Hub</a>
        <a href="https://with.it-sce.de/downloads/hub?os=windows" target="_blank" rel="noreferrer"
           style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, color: '#1f2937', textDecoration: 'none', fontSize: '0.875rem' }}><FaWindows style={{ verticalAlign: 'text-bottom', marginRight: 5 }} />Windows Hub</a>
        <a href="https://with.it-sce.de/downloads/hub?os=mac" target="_blank" rel="noreferrer"
           style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, color: '#1f2937', textDecoration: 'none', fontSize: '0.875rem' }}><FaApple style={{ verticalAlign: 'text-bottom', marginRight: 5 }} />Mac Hub</a>
      </div>
      {isOwner && (
        <button
          onClick={onCreateToken}
          style={{
            padding: '0.625rem 1.25rem', background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem',
          }}
        >+ Enrollment-Token für Headless-Server erstellen</button>
      )}
      <p style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#9ca3af' }}>
        Schritt-für-Schritt Hilfe:{' '}
        <a
          href="https://docs.it-sce.de/workspace/hub-einrichten"
          target="_blank"
          rel="noreferrer"
          style={{ color: '#2563eb', textDecoration: 'underline' }}
        >IT-SCE Hub einrichten</a>
      </p>
    </div>
  )
}

function DeviceCard({
  device,
  members,
  currentUserSub,
  onClick,
}: {
  device: Device
  members: OrgMember[]
  currentUserSub: string | null
  onClick: () => void
}) {
  const status = statusForDevice(device)
  const sc = statusColors[status]
  // TQ #5982: optisch differenzieren "meins" vs "geteilt".
  // Eigenes Geraet bekommt einen Accent-Border-Left, geteilte einen
  // dezenten Hinweis. Fremde owned-Devices (admin sieht alles) bekommen
  // den Owner-Namen mit grauem Hintergrund.
  const isMine = currentUserSub != null && device.owner_user_id === currentUserSub
  const isShared = device.owner_user_id == null
  return (
    <div onClick={onClick} style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderLeft: isMine ? '3px solid #2563eb' : '1px solid #e5e7eb',
      borderRadius: 10,
      padding: '1rem', cursor: 'pointer', transition: 'all 0.15s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#9ca3af'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)' }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }}><DeviceTypeIcon type={device.device_type} size={22} color="#374151" /></span>
          <div>
            <div style={{ fontWeight: 600, color: '#1f2937' }}>{device.hostname}</div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{fmtRelative(device.last_seen_at)}</div>
          </div>
        </div>
        <span style={{
          padding: '0.125rem 0.5rem', background: sc.bg, color: sc.text,
          borderRadius: 999, fontSize: '0.6875rem', fontWeight: 600, whiteSpace: 'nowrap',
        }}>{sc.label}</span>
      </div>
      {/* TQ #5982: Owner-Anzeige */}
      <div style={{
        marginTop: '0.625rem', fontSize: '0.75rem',
        color: isMine ? '#1d4ed8' : isShared ? '#92400e' : '#4b5563',
        display: 'flex', alignItems: 'center', gap: '0.375rem',
      }}>
        <span>👤</span>
        <span>
          {isMine ? 'Dein Gerät' : isShared ? 'Geteilt (Org-weit)' : `Owner: ${ownerDisplay(device.owner_user_id, members)}`}
        </span>
      </div>
      <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
        {device.tags.slice(0, 3).map((t) => (
          <span key={t} style={{
            padding: '0.125rem 0.5rem', background: '#f3f4f6', color: '#4b5563',
            borderRadius: 4, fontSize: '0.6875rem',
          }}>{t}</span>
        ))}
        {device.tags.length > 3 && (
          <span style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>+{device.tags.length - 3} more</span>
        )}
      </div>
      {device.enrolled_via && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.6875rem', color: '#9ca3af' }}>
          via {device.enrolled_via === 'hub-jwt' ? 'Hub' : 'CLI'}
        </div>
      )}
    </div>
  )
}

function DeviceDrawer({
  device,
  orgSlug,
  isOperator,
  isAdmin,
  members,
  personalMode = false,
  onClose,
  onOwnerChanged,
  onLifecycleChange,
}: {
  device: Device
  orgSlug: string
  isOperator: boolean
  isAdmin: boolean
  members: OrgMember[]
  personalMode?: boolean
  onClose: () => void
  onOwnerChanged: (updated: Device) => void
  onLifecycleChange: (removed: boolean) => void
}) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 'min(520px, 90vw)',
        background: '#fff', borderLeft: '1px solid #e5e7eb', padding: '1.5rem',
        overflowY: 'auto', boxShadow: '-4px 0 8px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>{device.hostname}</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '1.5rem',
            cursor: 'pointer', color: '#6b7280',
          }}>×</button>
        </div>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', fontSize: '0.875rem' }}>
          <dt style={{ color: '#6b7280' }}>Device-ID</dt>
          <dd style={{ margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>{device.device_id}</dd>
          <dt style={{ color: '#6b7280' }}>Typ</dt>
          <dd style={{ margin: 0 }}>{device.device_type}</dd>
          <dt style={{ color: '#6b7280' }}>Owner</dt>
          <dd style={{ margin: 0 }}>{ownerDisplay(device.owner_user_id, members)}</dd>
          <dt style={{ color: '#6b7280' }}>Agent-Version</dt>
          <dd style={{ margin: 0 }}>{device.agent_version}</dd>
          <dt style={{ color: '#6b7280' }}>Enrolled via</dt>
          <dd style={{ margin: 0 }}>{device.enrolled_via ?? '—'}</dd>
          <dt style={{ color: '#6b7280' }}>Enrolled am</dt>
          <dd style={{ margin: 0 }}>{new Date(device.enrolled_at).toLocaleString('de-DE')}</dd>
          <dt style={{ color: '#6b7280' }}>Last seen</dt>
          <dd style={{ margin: 0 }}>{fmtRelative(device.last_seen_at)}</dd>
          <dt style={{ color: '#6b7280' }}>Status</dt>
          <dd style={{ margin: 0 }}>{device.status}</dd>
        </dl>
        {device.description && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '0.25rem' }}>Beschreibung</div>
            <div style={{ fontSize: '0.875rem' }}>{device.description}</div>
          </div>
        )}
        {isAdmin && (
          <OwnerAssignment
            device={device}
            orgSlug={orgSlug}
            members={members}
            onOwnerChanged={onOwnerChanged}
          />
        )}
        {/* TQ #6962-T4: Remote-Aktionen, Confirm-Queue und Geräte-Grants sind
            heute org-scoped (POST /api/org/<slug>/devices/...). Im Personal-Mode
            (org_slug NULL) existiert kein org-Pfad → Panels ausblenden bis der
            owner-scoped Aria/Command-Pfad steht (SPEC §7, T5). */}
        {!personalMode && (
          <>
            <DeviceActionsPanel
              orgSlug={orgSlug}
              device={device}
              isOperator={isOperator && device.status !== 'decommissioned'}
              isAdmin={isAdmin}
              onLifecycleChange={onLifecycleChange}
            />
            <CommandConfirmPanel
              orgSlug={orgSlug}
              deviceId={device.device_id}
              isOperator={isOperator && device.status !== 'decommissioned'}
            />
            <DeviceGrantsPanel
              orgSlug={orgSlug}
              deviceId={device.device_id}
              members={members}
              isAdmin={isAdmin}
            />
          </>
        )}
        <div
          style={{
            marginTop: '1.5rem',
            padding: '0.75rem',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            fontSize: '0.8125rem',
            color: '#92400e',
          }}
        >
          {personalMode ? (
            <>
              <strong>Hinweis.</strong> Aria-Fernsteuerung (Befehle, Berechtigungen)
              für persönliche Geräte folgt in einer nächsten Iteration. Heute siehst
              du hier den Status deines Geräts.
            </>
          ) : (
            <>
              <strong>Phase 2 Iter 1.</strong> Sparklines (recharts), Capabilities-Detail-JSON,
              Tags-Edit und der org-weite Audit-Tab (CSV-Export, Filter) folgen in einer
              Phase 2 Iteration 2 (TQ-Folgeaufgabe).
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// OwnerAssignment — Org-Admin-only Dropdown im DeviceDrawer.
// PUT /api/org/<slug>/devices/<device_id>/owner { owner_user_id: uuid | null }
// (Backend live seit TQ #5981). Optimistic update via onOwnerChanged.
function OwnerAssignment({
  device,
  orgSlug,
  members,
  onOwnerChanged,
}: {
  device: Device
  orgSlug: string
  members: OrgMember[]
  onOwnerChanged: (updated: Device) => void
}) {
  const { api } = useAuth()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const currentValue = device.owner_user_id ?? '__shared__'

  const handleChange = async (next: string) => {
    setError(null)
    setSaving(true)
    const nextOwner: string | null = next === '__shared__' ? null : next
    try {
      await api!.put(`/api/org/${orgSlug}/devices/${device.device_id}/owner`, {
        owner_user_id: nextOwner,
      })
      onOwnerChanged({ ...device, owner_user_id: nextOwner })
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? 'Owner-Zuweisung fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{
      marginTop: '1.5rem',
      padding: '1rem',
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
    }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
        🔐 Berechtigung
      </div>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
        Org-Admin: weise das Gerät einem Mitglied zu (nur dieser sieht es im Cockpit)
        oder lass es <em>Geteilt</em>, damit alle Operatoren es sehen.
      </p>
      <label style={{ display: 'block', fontSize: '0.75rem', color: '#4b5563', marginBottom: '0.25rem' }}>
        Owner zuweisen
      </label>
      <select
        value={currentValue}
        disabled={saving || members.length === 0}
        onChange={(e) => void handleChange(e.target.value)}
        style={{
          width: '100%', padding: '0.5rem 0.625rem', borderRadius: 6,
          border: '1px solid #d1d5db', fontSize: '0.875rem', background: '#fff',
          cursor: saving ? 'wait' : 'pointer',
        }}
      >
        <option value="__shared__">Geteilt (Org-weit, alle Operatoren)</option>
        {members.map((m) => {
          const label = [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || displayIdentity(m)
          return (
            <option key={m.keycloak_id} value={m.keycloak_id}>
              {label} ({m.role})
            </option>
          )
        })}
      </select>
      {saving && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>Wird gespeichert…</div>
      )}
      {error && (
        <div style={{
          marginTop: '0.5rem', padding: '0.5rem 0.75rem',
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 6, color: '#b91c1c', fontSize: '0.8125rem',
        }}>{error}</div>
      )}
    </div>
  )
}

function TokenModal({
  orgSlug,
  onClose,
  onSuccess,
}: {
  orgSlug: string
  onClose: () => void
  onSuccess: () => void
}) {
  const { api } = useAuth()
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // TQ #5336: Toggle Hub-Flow (Desktop installiert Agent via Hub-UI) vs Headless (curl).
  const [installMode, setInstallMode] = useState<'hub' | 'headless'>('hub')

  const requestToken = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await api!.post<EnrollTokenResponse>(`/api/org/${orgSlug}/devices/enroll-token`, {})
      setToken(r.data?.token ?? null)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? 'Token konnte nicht erzeugt werden.')
    } finally {
      setLoading(false)
    }
  }, [api, orgSlug])

  // Phase 2: poll alle 5s GET /api/org/<slug>/devices und wenn neues
  // Device mit enrolled_via='cli-token' auftaucht -> auto-close.
  // Aktuell: kein Polling (sonst false-positives bei bestehenden devices).

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: '1.5rem 2rem',
        minWidth: 480, maxWidth: 640, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h2 style={{ marginTop: 0 }}>Gerät hinzufügen</h2>

        {/* TQ #5336: Wahl Hub vs Headless. */}
        <div style={{
          display: 'flex', gap: '0.5rem', marginBottom: '1rem',
          background: '#f3f4f6', padding: '0.25rem', borderRadius: 8,
        }}>
          <button
            onClick={() => setInstallMode('hub')}
            style={{
              flex: 1, padding: '0.5rem 0.75rem', borderRadius: 6, border: 'none',
              cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
              background: installMode === 'hub' ? '#fff' : 'transparent',
              color: installMode === 'hub' ? '#111827' : '#6b7280',
              boxShadow: installMode === 'hub' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            🖥️ Mit IT-SCE Hub (Mac/Win/Linux Desktop)
          </button>
          <button
            onClick={() => setInstallMode('headless')}
            style={{
              flex: 1, padding: '0.5rem 0.75rem', borderRadius: 6, border: 'none',
              cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
              background: installMode === 'headless' ? '#fff' : 'transparent',
              color: installMode === 'headless' ? '#111827' : '#6b7280',
              boxShadow: installMode === 'headless' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            }}
          >
            ⌨️ Headless-Server (SSH, ohne Hub)
          </button>
        </div>

        {installMode === 'hub' && (
          <div style={{
            padding: '0.75rem 1rem', background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 8, fontSize: '0.8125rem', color: '#1e40af', marginBottom: '1rem',
          }}>
            <strong>Empfohlen für Workstations / Macs / Notebooks.</strong><br />
            1. <a href="../downloads" style={{ color: '#1d4ed8' }}>IT-SCE Hub aus dem Downloads-Tab</a> installieren.<br />
            2. Im Hub mit Org-Login anmelden.<br />
            3. Im Hub: <em>Devices → Agent installieren</em>. Der Hub erledigt die Token-Anlage selbst.
          </div>
        )}

        {installMode === 'headless' && (
          <div style={{
            padding: '0.75rem 1rem', background: '#fef3c7', color: '#92400e',
            borderRadius: 8, fontSize: '0.8125rem', marginBottom: '1rem',
            border: '1px solid #fde68a',
          }}>
            <strong>Nur für Server ohne GUI.</strong> Workstations/Notebooks/Macs nutzen <strong>IT-SCE-Hub</strong> (Tab oben wechseln).
          </div>
        )}

        {installMode === 'headless' && !token && !error && (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <button onClick={requestToken} disabled={loading} style={{
              padding: '0.625rem 1.5rem', background: '#2563eb', color: '#fff',
              border: 'none', borderRadius: 8, cursor: loading ? 'wait' : 'pointer',
              fontSize: '0.875rem', fontWeight: 600,
            }}>{loading ? 'Erzeuge …' : 'Token erzeugen (24h einmal-gültig)'}</button>
          </div>
        )}

        {installMode === 'hub' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', background: '#fff', border: '1px solid #d1d5db',
              borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem',
            }}>Verstanden, schließen</button>
          </div>
        )}

        {error && (
          <div style={{
            padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 8, color: '#b91c1c', fontSize: '0.875rem', marginBottom: '1rem',
          }}>{error}</div>
        )}

        {installMode === 'headless' && token && (
          <>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.8125rem', color: '#6b7280' }}>Token:</div>
            <code style={{
              display: 'block', padding: '0.625rem 0.875rem', background: '#f3f4f6',
              borderRadius: 6, fontSize: '0.75rem', wordBreak: 'break-all', marginBottom: '1rem',
            }}>{token}</code>

            <h3 style={{ fontSize: '0.9375rem', marginTop: '1.5rem', marginBottom: '0.5rem' }}>Auf dem Server ausführen:</h3>

            <OSBlock label={<><FaLinux style={{ verticalAlign: 'text-bottom', marginRight: 5 }} />Linux</>} cmd={`curl -sSL https://cockpit.it-sce.de/install/agent.sh | sudo bash -s -- enroll ${token}`} />
            <OSBlock label={<><FaApple style={{ verticalAlign: 'text-bottom', marginRight: 5 }} />Mac</>} cmd={`curl -sSL https://cockpit.it-sce.de/install/agent-mac.sh | sudo bash -s -- enroll ${token}`} />
            <OSBlock label={<><FaWindows style={{ verticalAlign: 'text-bottom', marginRight: 5 }} />Windows</>} cmd={`irm https://cockpit.it-sce.de/install/agent.ps1 | iex; itsce-device-agent enroll ${token}`} />

            <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1rem' }}>
              Token gültig 24h, einmalig verwendbar. Wenn der Server beim portal-api anfragt, wird das Device automatisch
              in dieser Liste sichtbar.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button onClick={onClose} style={{
                padding: '0.5rem 1rem', background: '#fff', border: '1px solid #d1d5db',
                borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem',
              }}>Schließen</button>
              <button onClick={onSuccess} style={{
                padding: '0.5rem 1rem', background: '#10b981', color: '#fff',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
              }}>Liste aktualisieren</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function OSBlock({ label, cmd }: { label: ReactNode; cmd: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ fontSize: '0.8125rem', color: '#374151', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <code style={{
          flex: 1, padding: '0.5rem 0.75rem', background: '#1f2937', color: '#fff',
          borderRadius: 6, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>{cmd}</code>
        <button onClick={() => { void navigator.clipboard.writeText(cmd); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
          style={{
            padding: '0.5rem 0.75rem', background: '#fff', border: '1px solid #d1d5db',
            borderRadius: 6, cursor: 'pointer', fontSize: '0.75rem', whiteSpace: 'nowrap',
          }}>{copied ? '✓' : 'Copy'}</button>
      </div>
    </div>
  )
}
